import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from '@backstage/core-components';

/**
 * The revenexx product switcher (DX-275).
 *
 * A port of the public Developer Portal's own menu (its AppProductMenu), so
 * that a colleague who knows the switcher from revenexx.dev, Atlas or the
 * Cockpit meets the same thing here: the same products in the same order, the
 * same one-line descriptions, the same white overlay anchored to the logo.
 *
 * The list is the "Entwickler & Dienste" group from revenexx.com, carried over
 * verbatim rather than re-invented — a switcher that disagrees with the other
 * portals about what exists is worse than none.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

type Product = {
  label: string;
  subtitle: string;
  to: string;
  /** External destinations open in a new tab; the portal itself does not. */
  external?: boolean;
  /** The entry for the portal a reader is already in. */
  current?: boolean;
};

const PRODUCTS: Product[] = [
  {
    label: 'Developer Portal',
    subtitle: 'Start to develop with the Revenue Cloud.',
    to: 'https://revenexx.dev',
    external: true,
  },
  {
    label: 'Backstage',
    subtitle: 'Service catalog, docs and templates.',
    to: '/',
    current: true,
  },
  {
    label: 'Atlas',
    subtitle: 'Architecture, strategy and ADRs.',
    to: 'https://atlas.revenexx.dev',
    external: true,
  },
  {
    label: 'Help',
    subtitle: 'Learn how to use the Revenue Cloud.',
    to: 'https://help.revenexx.com',
    external: true,
  },
  {
    label: 'Cover',
    subtitle: 'Our design system for B2B storefronts.',
    to: 'https://cover.revenexx.design',
    external: true,
  },
  {
    label: 'Studio',
    subtitle: 'Unified UI for Apps inside the Cockpit.',
    to: 'https://studio.revenexx.design',
    external: true,
  },
  {
    label: 'Schemas',
    subtitle: 'Clear specifications for developers.',
    to: 'https://schemas.revenexx.com',
    external: true,
  },
  {
    label: 'Changelog',
    subtitle: 'Stay up to date with what we ship every day.',
    to: 'https://changelog.revenexx.com',
    external: true,
  },
  {
    label: 'Status',
    subtitle: 'Status insights and reports of all services.',
    to: 'https://status.revenexx.com',
    external: true,
  },
  {
    label: 'Support',
    subtitle: 'Contact us any time you need help.',
    to: 'https://support.revenexx.com',
    external: true,
  },
  {
    label: 'About revenexx',
    subtitle: 'Learn more about the Revenue Cloud.',
    to: 'https://revenexx.com',
    external: true,
  },
];

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    // Yields space before the actions on the right do: the logo stays, the
    // product name is what a narrow window can afford to lose.
    minWidth: 0,
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  logo: {
    height: 24,
    display: 'block',
  },
  /*
    The Developer Portal's own two logos, carried over verbatim (DX-352): on
    the dark bar its `logo-changelog.svg`, a pale violet wordmark rather than
    white; on the violet bar of the dark appearance `logo-dark.svg`, whose
    wordmark is yellow. Two images switched by `.dark`, as the Developer
    Portal does it, so the logo never waits on the theme to resolve.
  */
  logoLight: {
    '.dark &': { display: 'none' },
  },
  logoDark: {
    display: 'none',
    '.dark &': { display: 'block' },
  },
  divider: {
    width: 1,
    height: 20,
    background: 'rgba(255, 255, 255, 0.15)',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  /*
    The "INTERNAL" badge from the target picture (DX-302).

    It answers a question the product switcher beside it does not: the switcher
    says WHICH revenexx surface this is, the badge says this one is not the
    public one. Both are worth having — revenexx.dev carries the same brand and
    is open to anyone, and a reader arriving here from it has no other cue that
    what they are looking at is inside the company.

    Yellow from the brand ramp rather than the mockup's own hex: the same
    colour the target picture uses, taken from where the portal's colours are
    written down. It is the one place in the shell a full-strength brand colour
    is right — the badge IS the information, which is the test tokens.css sets
    for using this ramp at all.
  */
  badge: {
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: 'var(--radius-sm, 4px)',
    background: 'var(--color-brand-yellow-2)',
    // Near-black on yellow: the brand ink, which clears contrast comfortably
    // where white on the same yellow would not.
    color: '#0B0522',
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.6,
    textTransform: 'uppercase',
    // The bar is one row competing for width. The badge is the first thing to
    // go when there is none — the switcher and the account menu are ways out
    // of the page, and this is a label.
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    border: 'none',
    background: 'none',
    color: 'var(--ui-bar-ink)',
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    '&:hover': { color: '#FFFFFF' },
    // Yellow beside the yellow wordmark, white on hover — the portals' site
    // name in the dark appearance (`dark:text-brand-secondary`).
    '.dark &': {
      color: 'var(--color-brand-secondary)',
      '&:hover': { color: '#FFFFFF' },
    },
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  chevron: {
    fontSize: '1rem',
    transition: 'transform 0.2s ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  /**
   * The overlay. Offsets are the reference's: it opens over the top bar with
   * its own logo sitting exactly where the bar's logo was, which is what makes
   * it read as the bar unfolding rather than a panel appearing next to it.
   */
  menu: {
    position: 'absolute',
    left: -11,
    top: -10,
    zIndex: theme.zIndex.modal,
    width: '21rem',
    maxWidth: 'calc(100vw - 32px)',
    overflow: 'hidden',
    borderRadius: 6,
    background: '#FFFFFF',
    boxShadow:
      '0 6px 16px -4px rgba(11, 5, 34, 0.30), 0 30px 70px -12px rgba(11, 5, 34, 0.62)',
    border: '1px solid rgba(11, 5, 34, 0.10)',
  },
  menuHeader: {
    padding: '10px 20px 12px 11px',
  },
  menuLogo: {
    height: 22,
    display: 'block',
  },
  menuRule: {
    height: 1,
    background: 'rgba(11, 5, 34, 0.08)',
  },
  close: {
    position: 'absolute',
    right: 8,
    top: 8,
    display: 'grid',
    placeItems: 'center',
    width: 28,
    height: 28,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'none',
    color: 'var(--color-brand-400)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-brand-50)',
      color: 'var(--color-brand-700)',
    },
  },
  closeIcon: { fontSize: '1rem' },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 6,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: '8px 10px',
    borderRadius: 6,
    textDecoration: 'none',
    '&:hover': { background: 'var(--color-brand-50)' },
    '&:hover $arrow': {
      transform: 'translateX(2px)',
      color: 'var(--color-brand-900)',
    },
  },
  itemText: { minWidth: 0, flex: 1 },
  itemLabel: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--color-brand-950)',
  },
  itemLabelCurrent: { color: 'var(--color-brand-600)' },
  itemSubtitle: {
    display: 'block',
    marginTop: 2,
    fontSize: '0.75rem',
    color: '#71717A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  arrow: {
    marginTop: 6,
    fontSize: '1rem',
    flexShrink: 0,
    color: 'var(--color-brand-300)',
    transition: 'all 0.15s ease',
  },
}));

export const ProductMenu = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // Close on Escape and on a click outside — the two ways a reader expects to
  // dismiss an overlay they opened by accident.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onClick = (e: MouseEvent) => {
      if (
        root.current &&
        e.target instanceof Node &&
        !root.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={classes.root} ref={root}>
      <Link
        to="/"
        className={classes.logoLink}
        aria-label="revenexx Developer Portal home"
        underline="none"
      >
        <img
          src="/images/logos/REVENEXX-LOGO-BAR-RGB.svg"
          alt="revenexx"
          className={`${classes.logo} ${classes.logoLight}`}
        />
        <img
          src="/images/logos/REVENEXX-LOGO-DARK-RGB.svg"
          alt="revenexx"
          className={`${classes.logo} ${classes.logoDark}`}
        />
      </Link>

      <span className={classes.divider} aria-hidden="true" />

      <button
        type="button"
        className={classes.trigger}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="revenexx products & services"
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
      >
        Backstage
        <ExpandMoreIcon
          className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`}
        />
      </button>

      {/* Not aria-hidden: "this portal is internal" is information a reader
          using a screen reader needs as much as a sighted one. */}
      <span className={classes.badge}>Internal</span>

      {open && (
        <div className={classes.menu} role="menu">
          <button
            type="button"
            className={classes.close}
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            <CloseIcon className={classes.closeIcon} />
          </button>

          <div className={classes.menuHeader}>
            <img
              src="/revenexx-logo.svg"
              alt="revenexx"
              className={classes.menuLogo}
            />
          </div>
          <div className={classes.menuRule} />

          <ul className={classes.list}>
            {PRODUCTS.map(product => {
              const Component = (product.external ? 'a' : Link) as any;
              return (
                <li key={product.label}>
                  {/*
                  An internal destination routes inside the app; a raw <a> there
                  would reload the whole portal to reach a page it already has.
                */}
                  <Component
                    {...(product.external
                      ? {
                          href: product.to,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : { to: product.to, underline: 'none' as const })}
                    className={classes.item}
                    onClick={() => setOpen(false)}
                    role="menuitem"
                  >
                    <span className={classes.itemText}>
                      <span
                        className={`${classes.itemLabel} ${
                          product.current ? classes.itemLabelCurrent : ''
                        }`}
                      >
                        {product.label}
                      </span>
                      <span className={classes.itemSubtitle}>
                        {product.subtitle}
                      </span>
                    </span>
                    <ArrowForwardIcon className={classes.arrow} />
                  </Component>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
